import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { now } from 'moment-timezone';
import _ from 'lodash';

import { openModalAction } from '../../../../actions/modal';
import Strings from '../../../../Strings';
import { actions } from '../redux/actions';
import MedicalHistory from './MedicalHistory';
import PatientDetails from './PatientDetails';
import DashboardLayout from '../../../Dashboards/DashboardLayout';
import Insurance from './Insurance';
import CenterDetails from './CenterDetails';
import TransplantDetails from './TransplantDetails';
import { OnboardPatientModalPages } from '../../../../modals/_ModalsMetadata/ModalsMetadata';
import Caregivers from './Caregivers';
import { hasPermission, ORG_FEATURES, orgFeatureEnabled, PERMISSIONS } from '../../../../utils/userPermissions';
import { RPM_PATIENT_STATUS } from '../../../../constants';
import { OnboardStages } from '../../../../modals/OnboardPatientWizard/helpers';
import { getProgramsFromPatient } from '../../../../utils/cmsPrograms';
import { EXTERNAL_PROVIDER_TYPES } from '../../../../modals/EditExternalProviderModal';
import ExternalProviderDetails from './ExternalProviderDetails';

export const DASHBOARD_ID = 'DashboardPatientInfo3';

const medicalHistoryMetadata = {
  id: MedicalHistory.widgetId,
  name: Strings.widgets.medicalHistory,
  permission: '',
  defProps: { i: MedicalHistory.widgetId, w: 4, h: 1, minW: 2, minH: 3 },
};
const patientDetailsMetadata = {
  id: PatientDetails.widgetId,
  name: Strings.widgets.patientDetails,
  permission: '',
  defProps: { i: PatientDetails.widgetId, w: 4, h: 1, minW: 2, minH: 3 },
};
const insuranceMetadata = {
  id: Insurance.widgetId,
  name: Strings.widgets.insurance,
  permission: '',
  defProps: { i: Insurance.widgetId, w: 4, h: 6, minW: 2, minH: 6 },
};
const centerDetailsMetadata = {
  id: CenterDetails.widgetId,
  name: Strings.widgets.centerDetails,
  permission: '',
  defProps: { i: CenterDetails.widgetId, w: 4, h: 1, minW: 2, minH: 3 },
};
const transplantDetailsMetadata = {
  id: TransplantDetails.widgetId,
  name: Strings.widgets.transplantDetails,
  permission: '',
  defProps: { i: TransplantDetails.widgetId, w: 4, h: 1, minW: 2, minH: 3 },
};
const caregiversMetadata = {
  id: Caregivers.widgetId,
  name: Strings.caregivers,
  permission: '',
  defProps: { i: Caregivers.widgetId, w: 4, h: 1, minW: 2, minH: 3 },
};
const primaryCareProviderMetadata = {
  id: ExternalProviderDetails.widgetId,
  name: Strings.primaryCareProvider,
  permission: '',
  defProps: { i: ExternalProviderDetails.widgetId, w: 4, h: 7, minW: 2, minH: 7 },
};

export const WidgetMetadata = [
  medicalHistoryMetadata,
  patientDetailsMetadata,
  insuranceMetadata,
  centerDetailsMetadata,
  transplantDetailsMetadata,
  caregiversMetadata,
  primaryCareProviderMetadata,
];

const l = [
  { ...medicalHistoryMetadata.defProps, x: 0, y: 4, w: 6, h: 5 },
  { ...patientDetailsMetadata.defProps, x: 0, y: 0, w: 6, h: 10 },
  { ...insuranceMetadata.defProps, x: 6, y: 0, w: 6, h: 6 },
  { ...centerDetailsMetadata.defProps, x: 6, y: 2, w: 6, h: 5 },
  { ...transplantDetailsMetadata.defProps, x: 6, y: 5, w: 6, h: 3 },
  { ...caregiversMetadata.defProps, x: 6, y: 7, w: 6, h: 3 },
  { ...primaryCareProviderMetadata.defProps, x: 6, y: 10, w: 6, h: 7 },
];

const m = [
  { ...medicalHistoryMetadata.defProps, x: 0, y: 4, w: 4, h: 5 },
  { ...patientDetailsMetadata.defProps, x: 0, y: 0, w: 4, h: 10 },
  { ...insuranceMetadata.defProps, x: 4, y: 0, w: 4, h: 6 },
  { ...centerDetailsMetadata.defProps, x: 6, y: 2, w: 4, h: 5 },
  { ...transplantDetailsMetadata.defProps, x: 4, y: 5, w: 4, h: 3 },
  { ...caregiversMetadata.defProps, x: 6, y: 7, w: 4, h: 3 },
  { ...primaryCareProviderMetadata.defProps, x: 4, y: 10, w: 4, h: 7 },
];

const s = [
  { ...medicalHistoryMetadata.defProps, x: 0, y: 4, w: 4, h: 5 },
  { ...patientDetailsMetadata.defProps, x: 0, y: 0, w: 4, h: 10 },
  { ...insuranceMetadata.defProps, x: 0, y: 2, w: 4, h: 6 },
  { ...centerDetailsMetadata.defProps, x: 0, y: 3, w: 4, h: 5 },
  { ...transplantDetailsMetadata.defProps, x: 0, y: 4, w: 4, h: 3 },
  { ...caregiversMetadata.defProps, x: 6, y: 7, w: 4, h: 3 },
  { ...primaryCareProviderMetadata.defProps, x: 0, y: 10, w: 4, h: 7 },
];

export const DefaultLayouts = { l, m, s };

function PatientInfo(props) {
  const { patientDetails, enrollmentId } = props;

  const [widgets, setWidgets] = useState([]);
  const [refreshTimestamp, setRefreshTimestamp] = useState(now());
  const scrollbarRef = React.useRef();
  const showTransplantDetails = orgFeatureEnabled(ORG_FEATURES.transplant_details_enabled);

  const onEditPatient = (event, patient, pageNo) => {
    event.preventDefault();
    event.stopPropagation();
    props.onEditPatient(patient, pageNo, () => setRefreshTimestamp(now()));
  };

  useEffect(() => {
    props.getPatientInfo(props.patientId);
  }, [props.patientId, refreshTimestamp]);

  useEffect(() => {
    initializeDashboard();
  }, [patientDetails, props.refreshTimestamp, enrollmentId]);

  const initializeDashboard = () => {
    const isPatientDisenrolled = patientDetails?.enrollmentStates?.every(state => state === RPM_PATIENT_STATUS.ended);
    const canEdit = hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_INFO_EDIT) && !isPatientDisenrolled;
    const canEditProvider = hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_INFO_EDIT);

    const widgetMedHist = {
      ...medicalHistoryMetadata,
      render: (
        <MedicalHistory
          conditions={patientDetails?.conditions}
          programs={getProgramsFromPatient(patientDetails)}
          enrollmentId={enrollmentId}
          medications={patientDetails?.medications}
          onEdit={canEdit ? e => onEditPatient(e, patientDetails, [OnboardPatientModalPages.medicalHistory.id]) : undefined}
        />
      ),
      noPadding: true,
    };
    const widgetPatDetails = {
      ...patientDetailsMetadata,
      render: (
        <PatientDetails
          patientDetails={patientDetails}
          onEdit={
            canEdit
              ? e =>
                  onEditPatient(e, patientDetails, [
                    OnboardPatientModalPages.patientDetails.id,
                    OnboardPatientModalPages.contactInformation.id,
                  ])
              : undefined
          }
        />
      ),
      noPadding: true,
    };
    const widgetIns = {
      ...insuranceMetadata,
      render: (
        <Insurance
          insurance={patientDetails?.insurance}
          onEdit={canEdit ? e => onEditPatient(e, patientDetails, [OnboardPatientModalPages.insurance.id]) : undefined}
        />
      ),
      noPadding: true,
    };
    const widgetCentDet = {
      ...centerDetailsMetadata,
      render: (
        <CenterDetails
          centerDetails={patientDetails?.centerDetails}
          providers={patientDetails?.providers}
          facility={patientDetails?.facilityName}
          cohort={patientDetails?.cohortName}
          onEdit={canEdit ? e => onEditPatient(e, patientDetails, [OnboardPatientModalPages.centerDetails.id]) : undefined}
        />
      ),
      noPadding: true,
    };
    const widgetTransplDet = {
      ...transplantDetailsMetadata,
      render: (
        <TransplantDetails
          transplantDetails={patientDetails?.transplantDetails}
          onEdit={canEdit ? e => onEditPatient(e, patientDetails, [OnboardPatientModalPages.transplantDetails.id]) : undefined}
        />
      ),
      noPadding: true,
    };
    const widgetCaregivers = {
      ...caregiversMetadata,
      render: <Caregivers patientId={patientDetails?.id} refreshTimestamp={props.refreshTimestamp} canEdit={canEdit} />,
      noPadding: true,
    };
    const widgetPrimaryCareProvider = {
      ...primaryCareProviderMetadata,
      render: (
        <ExternalProviderDetails
          provider={patientDetails?.primaryCareProvider}
          providerType={EXTERNAL_PROVIDER_TYPES.primaryCare}
          patientId={patientDetails?.masked_id}
          onRefresh={props.onRefresh}
          editable={canEditProvider}
        />
      ),
      noPadding: true,
    };

    setWidgets(
      [widgetMedHist, widgetPatDetails, widgetIns, widgetCentDet, widgetCaregivers, widgetPrimaryCareProvider].concat(
        showTransplantDetails ? [widgetTransplDet] : [],
      ),
    );
  };

  return (
    widgets.length > 0 &&
    !_.isEmpty(DefaultLayouts) && (
      <OverlayScrollbarsComponent
        defer
        className="patient-info-dashboard scrollbar-right-margin"
        options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
        ref={scrollbarRef}
      >
        <DashboardLayout
          dashboardId={DASHBOARD_ID}
          widgets={widgets}
          defaultLayouts={DefaultLayouts}
          breakpoints={{ l: 1400, m: 1151, s: 0 }}
          rowHeight={40}
          scrollbarRef={scrollbarRef}
        />
        {/* <EnrollmentHistory patientId={patientDetails?.id}/> */}
      </OverlayScrollbarsComponent>
    )
  );
}

PatientInfo.propTypes = {
  getPatientInfo: PropTypes.func,
  isLoading: PropTypes.any,
  onEditPatient: PropTypes.func,
  patientDetails: PropTypes.shape({
    masked_id: PropTypes.string,
    centerDetails: PropTypes.arrayOf(
      PropTypes.shape({
        centerName: PropTypes.string,
        provider: PropTypes.string,
        clinicContact: PropTypes.string,
      }),
    ),
    providers: PropTypes.arrayOf(PropTypes.string),
    dateOfBirth: PropTypes.any,
    email: PropTypes.any,
    homeAddress: PropTypes.any,
    insurance: PropTypes.arrayOf(
      PropTypes.shape({
        insuranceName: PropTypes.string,
        policyId: PropTypes.string,
        groupNumber: PropTypes.string,
        payerId: PropTypes.string,
        insuranceAndBillingNotes: PropTypes.string,
      }),
    ),
    medicalHistory: PropTypes.arrayOf(
      PropTypes.shape({
        primaryCondition: PropTypes.string,
        secondaryCondition: PropTypes.string,
      }),
    ),
    mrn: PropTypes.any,
    patientName: PropTypes.any,
    primaryPhoneNo: PropTypes.any,
    textPhoneNo: PropTypes.any,
    transplantDetails: PropTypes.arrayOf(
      PropTypes.shape({
        organType: PropTypes.string,
        transplantDate: PropTypes.string,
      }),
    ),
  }),
  patientId: PropTypes.any,
  onRefresh: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  const { patients } = state.superUser;
  return {
    patientDetails: patients?.patientDetails,
    isLoading: patients?.isLoading,
  };
};

const mapDispatchToProps = dispatch => ({
  onNavigate: path => dispatch(push(path)),
  getPatientInfo: patientId => dispatch(actions.getPatientInfo(patientId)),
  onEditPatient: (patient, pages, nextAction) =>
    dispatch(
      openModalAction('onboard-patient', {
        onSuccess: nextAction,
        patient,
        stage: OnboardStages.editPatient,
        pages,
        ignoreFilters: true,
      }),
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientInfo);
